/**
 * merge-contact-dialog.tsx — "Gộp với khách khác" ngay trên hồ sơ khách: tìm khách cần gộp → chọn → xác nhận.
 * Dùng POST /contacts/merge (gộp theo từng trường, hoàn tác được ở Khách trùng lặp → Lịch sử gộp). Khách đang xem là khách CHÍNH.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import { Loader2, Search } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { apiError } from '@/lib/api-client'
import { useContacts, useMergeContacts, type ContactDetail, type ContactListItem } from '@/hooks/use-contacts'

export function MergeContactDialog({ contact, open, onOpenChange }: { contact: ContactDetail; open: boolean; onOpenChange: (v: boolean) => void }) {
  const { t } = useTranslation('custList')
  const [q, setQ] = useState('')
  const [picked, setPicked] = useState<ContactListItem | null>(null)
  const merge = useMergeContacts()
  const navigate = useNavigate()
  const search = q.trim()
  const { data, isFetching } = useContacts({ search: search || undefined, limit: 8, page: 1 })
  const name = contact.crmName || contact.fullName || t('mergeContact.thisCustomer')
  const nameOf = (c: ContactListItem) => c.crmName || c.fullName || t('mergeContact.noName')
  const results = search.length >= 2 ? (data?.contacts ?? []).filter((c) => c.id !== contact.id) : []

  const close = (v: boolean) => {
    if (merge.isPending) return
    if (!v) { setQ(''); setPicked(null) }
    onOpenChange(v)
  }
  const submit = () => {
    if (!picked) return
    merge.mutate({ primaryId: contact.id, mergeIds: [picked.id] }, {
      onSuccess: () => {
        toast.success(t('mergeContact.merged', { from: nameOf(picked), to: name }))
        close(false)
        navigate(`/customers/${contact.id}`, { replace: true })
      },
      onError: (e) => toast.error(apiError(e)),
    })
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('mergeContact.title')}</DialogTitle>
          <DialogDescription>
            <Trans t={t} i18nKey="mergeContact.description" values={{ name }} components={{ b: <b /> }} />
          </DialogDescription>
        </DialogHeader>
        {!picked ? (
          <div className="space-y-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('mergeContact.searchPlaceholder')} className="pl-8" />
            </div>
            <div className="max-h-64 overflow-y-auto rounded-md border">
              {search.length < 2 ? (
                <p className="p-3 text-sm text-muted-foreground">{t('mergeContact.minChars')}</p>
              ) : isFetching && !results.length ? (
                <p className="flex items-center gap-2 p-3 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {t('mergeContact.searching')}</p>
              ) : results.length === 0 ? (
                <p className="p-3 text-sm text-muted-foreground">{t('mergeContact.noMatch')}</p>
              ) : (
                results.map((c) => (
                  <button key={c.id} type="button" onClick={() => setPicked(c)} className="flex w-full flex-col items-start border-b px-3 py-2 text-left last:border-0 hover:bg-muted/60">
                    <span className="text-sm font-medium">{nameOf(c)}</span>
                    <span className="text-xs text-muted-foreground">{[c.phone, c.email].filter(Boolean).join(' · ') || t('mergeContact.noContact')}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-2 rounded-md border bg-muted/30 p-3 text-sm">
            <p>
              <Trans
                t={t}
                i18nKey="mergeContact.confirm"
                values={{
                  from: nameOf(picked),
                  fromPhone: picked.phone ? ` (${picked.phone})` : '',
                  to: name,
                  toPhone: contact.phone ? ` (${contact.phone})` : '',
                }}
                components={{ b: <b /> }}
              />
            </p>
            <button type="button" className="text-xs text-primary hover:underline" onClick={() => setPicked(null)}>{t('mergeContact.pickOther')}</button>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => close(false)} disabled={merge.isPending}>{t('common:actions.cancel')}</Button>
          <Button onClick={submit} disabled={!picked || merge.isPending}>
            {merge.isPending && <Loader2 className="h-4 w-4 animate-spin" />} {t('mergeContact.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
